import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { RailCap } from "../../../../../http/border/types/railCaps";
import { RailCorner } from "../../../../../http/border/types/railCorners";
import { RailWallFixing } from "../../../../../http/border/types/railWallFixings";
import { Rail, UpdateRailBody } from "../../../../../http/border/types/rails";
import { useRailCapsApi } from "../../../../../http/border/useRailCaps";
import { useRailCornersApi } from "../../../../../http/border/useRailCorners";
import { useRailWallFixingsApi } from "../../../../../http/border/useRailWallFixings";
import { useRailsApi } from "../../../../../http/border/useRails";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";
import Switch from "../../../../ui/Switch";

interface RailFormBody {
  item: SearchAdminItem | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  length: number;
  diameter: number;
  isReinforced: boolean;
  railCap?: RailCap | null;
  railWallFixing?: RailWallFixing | null;
  railCorner?: RailCorner | null;
}

interface RailFormProps {
  rail?: Rail;
  onClose: () => void;
}

const RailForm: FC<RailFormProps> = ({ onClose, rail }) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);
  const [defaultRailCapOptions, setDefaultRailCapOptions] = useState<RailCap[]>([]);
  const [defaultRailWallFixingOptions, setDefaultRailWallFixingOptions] = useState<RailWallFixing[]>([]);
  const [defaultRailCornerOptions, setDefaultRailCornerOptions] = useState<RailCorner[]>([]);

  const itemsApi = useItemsApi();
  const railCapsApi = useRailCapsApi();
  const railCornersApi = useRailCornersApi();
  const railWallFixingsApi = useRailWallFixingsApi();
  const railsApi = useRailsApi();

  const defaultValues = {
    item: rail?.item ?? null,
    order: rail?.order ?? 50,
    visibility: rail?.visibility ?? "hidden",
    length: rail?.length,
    isReinforced: rail?.isReinforced,
    diameter: rail?.diameter,
    railCap: rail?.railCap ?? null,
    railWallFixing: rail?.railWallFixing ?? null,
    railCorner: rail?.railCorner ?? null,
  };

  return (
    <Form<RailFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        const common: UpdateRailBody = {
          order: formData.order!,
          visibility: formData.visibility,
          length: formData.length,
          isReinforced: formData?.isReinforced,
          diameter: formData?.diameter,
          railCapId: formData.railCap?.item.id,
          railWallFixingId: formData.railWallFixing?.item.id,
          railCornerId: formData.railCorner?.item.id,
        };
        if (rail)
          try {
            await railsApi.updateAdminRail(rail.item.id, common);
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        else
          try {
            await railsApi.insertAdminRail({
              ...common,
              itemId: formData.item?.id ?? "",
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
      }}
    >
      {({ control, setValue, watch, reset }) => {
        useEffect(() => {
          defaultValues && reset(defaultValues);
        }, [rail]);

        const isReinforced = watch("isReinforced");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={itemLabel}
                            isDisabled={!!rail?.item.id}
                            getOptionValue={(x) => x.id}
                            isInvalid={fieldState.invalid}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="railCap"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.railCaps.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, railCapsApi.getAdminRailCaps);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultRailCapOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultRailCapOptions}
                        loadOptions={loadOptions}
                        isClearable
                        getOptionLabel={(x) => itemLabel(x.item)}
                        getOptionValue={(x) => x.item.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("railCap", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="railWallFixing"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.railWallFixings.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(
                      aborter,
                      searchTerm,
                      railWallFixingsApi.getAdminRailWallFixings,
                    );

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultRailWallFixingOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultRailWallFixingOptions}
                        loadOptions={loadOptions}
                        isClearable
                        getOptionLabel={(x) => itemLabel(x.item)}
                        getOptionValue={(x) => x.item.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("railWallFixing", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="railCorner"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.railCorners.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, railCornersApi.getAdminRailCorners);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultRailCornerOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultRailCornerOptions}
                        loadOptions={loadOptions}
                        isClearable
                        getOptionLabel={(x) => itemLabel(x.item)}
                        getOptionValue={(x) => x.item.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("railCorner", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="length"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.length} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      suffix=" mm"
                      allowNegative={false}
                      onBlur={(value) => value && setValue("length", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="diameter"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.diameter} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      suffix=" mm"
                      allowNegative={false}
                      onBlur={(value) => value && setValue("diameter", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.isReinforced} isOptional>
              {({ labelId }) => (
                <Switch id={labelId} checked={isReinforced} onChange={(checked) => setValue("isReinforced", checked)} />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default RailForm;
